
import { useState, useEffect } from "react";
import { supabase, fetchGuidanceForRequirement } from "@/integrations/supabase/client";
import { Project, Requirement, Guidance } from "@/types/project";
import { toast } from "sonner";

export function useProjectDetails(projectId: string | undefined) {
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedGuidance, setSelectedGuidance] = useState<Guidance | null>(null);
  const [selectedRequirementText, setSelectedRequirementText] = useState<string>("");
  const [isGuidanceDrawerOpen, setIsGuidanceDrawerOpen] = useState(false);
  
  useEffect(() => {
    if (projectId) {
      fetchProjectDetails(projectId);
    }
  }, [projectId]);
  
  const fetchProjectDetails = async (id: string) => {
    try {
      setIsLoading(true);
      
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select(`
          *,
          standards:standard_id (
            id,
            title,
            category,
            identifier,
            version,
            scope,
            purpose,
            status
          )
        `)
        .eq('id', id)
        .single();
      
      if (projectError) throw projectError;
      
      if (projectData) {
        const { data: requirementsData, error: requirementsError } = await supabase
          .from('requirements')
          .select('*, requirement_type, section, subsection, requirement_text')
          .eq('standard_id', projectData.standards.id);
        
        if (requirementsError) throw requirementsError;
        
        const formattedProject = {
          ...projectData,
          standard: projectData.standards,
          requirements: requirementsData || []
        };
        
        setProject(formattedProject);
      }
    } catch (error: any) {
      console.error("Error fetching project details:", error);
      toast.error(error.message || "Error loading project details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewGuidance = async (requirement: Requirement) => {
    try {
      const guidance = await fetchGuidanceForRequirement(requirement.id);
      
      if (guidance) {
        setSelectedGuidance(guidance);
        setSelectedRequirementText(requirement.requirement_text || requirement.text || '');
        setIsGuidanceDrawerOpen(true);
      } else {
        toast.warning(`No guidance available for ${requirement.identifier}`);
      }
    } catch (error) {
      console.error("Error fetching guidance:", error);
      toast.error("Failed to load guidance information");
    }
  };

  const handleUploadDocumentation = (requirement: Requirement) => {
    toast.info(`Upload documentation for ${requirement.identifier}`);
  };

  const handleMarkNotApplicable = (requirement: Requirement) => {
    toast.info(`Marked ${requirement.identifier} as not applicable`);
  };

  return {
    project,
    isLoading,
    selectedGuidance,
    selectedRequirementText,
    isGuidanceDrawerOpen,
    setIsGuidanceDrawerOpen,
    handleViewGuidance,
    handleUploadDocumentation,
    handleMarkNotApplicable
  };
}
